import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Link,
  Typography,
} from '@mui/material';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white,
  orange50,
  linkColor,
  red50,
  blue20,
  blue50,
} from '../../../constants/colors';

import { EMAIL_INPUT_PLACEHOLDER } from '../../../constants/placeholders';
import { EMAIL_LABEL, REQUIRED_LABEL } from '../../../constants/labels';

import { AuthContext } from '../../../contexts/auth';
import { headerHeight, spHeaderHeight } from '../../../constants/location';
import { authRegisterSchema } from '../../../validation/user';
import type { AuthRegisterType } from '../../../types/user';

import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { formLabelWrapper } from '../../../constants/location';
import { CORPORATE_TERMS_URL, CORPORATE_PRIVACY_URL } from '../../../constants/externalUrls';
import Header from '../../uiParts/menu/Header';
import MetaTitle from '../../utils/MetaTitle';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../api/firebase/config';
import { validateReferralToken } from '../../../api/firebase/function/referralFunction';
import { validateCouponCode } from '../../../api/firebase/function/couponFunction';
import { referralMessages, couponMessages } from '../../../constants/messages';

const styles = {
  formLabelWrapper,
  header: {
    height: headerHeight,
    position: 'fixed',
    '@media screen and (max-width:600px)': {
      height: spHeaderHeight,
      width: '100%',
      zIndex: '100',
    },
  },
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 960px)': {
      width: '-webkit-fill-available',
    },
  },
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'row',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column',
      minHeight: '100vh',
    },
  },
  inviteFormContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 960px)': {
      width: '100%',
      maxWidth: '480px',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      p: '32px 16px',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    '@media screen and (max-width:960px)': {
      width: '100%',
      padding: '0 16px',
      boxSizing: 'border-box',
    },
    width: '560px',
    margin: '0 auto',
  },
  link: {
    color: gray100,
    display: 'block',
    fontWeight: '500',
    m: '24px 0px 0px 0px',
  },
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
  label: {
    fontSize: '14px',
  },
  termLabel: {
    color: gray100,
    fontSize: '12px',
  },
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0,
    },
    marginBottom: '16px',
  },
  referralBanner: {
    backgroundColor: blue20,
    border: `1px solid ${blue50}`,
    borderRadius: '6px',
    padding: '12px 16px',
    boxSizing: 'border-box',
    textAlign: 'center',
    width: '100%',
    maxWidth: '480px',
  },
  referralBannerTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: blue50,
    marginBottom: '4px',
  },
  referralBannerText: {
    fontSize: '13px',
    color: gray100,
  },
};

const AuthRegister: React.FC = (): JSX.Element => {
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [bannerTitle, setBannerTitle] = useState<string>('');
  const [bannerText, setBannerText] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  // URLパラメータの ?ref= または ?coupon= を検証してバナー表示
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refToken = urlParams.get('ref');
    const couponCode = urlParams.get('coupon');

    if (refToken) {
      const checkToken = async (): Promise<void> => {
        try {
          const result = await validateReferralToken(refToken);
          if (result.valid && result.data) {
            setBannerTitle(referralMessages.bannerTitle(result.data.inviter_company_name));
            setBannerText(referralMessages.bannerText);
          }
        } catch {
          // トークン無効時はバナー非表示のまま通常フロー
        }
      };
      void checkToken();
    } else if (couponCode) {
      const checkCoupon = async (): Promise<void> => {
        try {
          const result = await validateCouponCode(couponCode);
          if (result.valid && result.data) {
            const amount = result.data.reward_amount;
            const pages = Math.floor(amount / 50);
            setBannerTitle(couponMessages.bannerTitle);
            setBannerText(couponMessages.bannerText(amount, pages));
          }
        } catch {
          // 無効時はバナー非表示
        }
      };
      void checkCoupon();
    }
  }, [location.search]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AuthRegisterType>({
    resolver: yupResolver(authRegisterSchema),
    defaultValues: {
      email: '',
      privacyCheck: false,
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        Boolean(errors.email) ||
        watch('privacyCheck') === undefined ||
        false ||
        Boolean(errors.privacyCheck)
      );
    }
    return false;
  };

  const checkboxHelper = (): string | undefined => {
    if (errors.privacyCheck === undefined) return;
    return String(errors.privacyCheck.message);
  };

  // 会員登録メール送信処理
  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = watch('email');

      // URLパラメータからリファラルトークン or クーポンコードを取得
      const urlParams = new URLSearchParams(location.search);
      const referralToken = urlParams.get('ref') ?? undefined;
      const couponCode = urlParams.get('coupon') ?? undefined;

      // Cloud Function呼び出し
      const sendUserRegistration = httpsCallable<
        { email: string; referralToken?: string; couponCode?: string },
        { success: boolean; message: string }
      >(functions, 'sendUserRegistration');
      const result = await sendUserRegistration({ email, referralToken, couponCode });

      if (result.data.success) {
        navigate('/auth/register/sent');
      } else {
        setErrorText('メール送信に失敗しました。');
        setOpen(true);
      }
    } catch (error: unknown) {
      // エラーメッセージの処理
      let errorMessage = 'メール送信中にエラーが発生しました。';

      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        if (firebaseError.code === 'functions/already-exists') {
          errorMessage = 'このメールアドレスは既に登録されています。';
        } else if (firebaseError.code === 'functions/invalid-argument') {
          errorMessage = 'メールアドレスが正しくありません。';
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        }
      }

      setErrorText(errorMessage);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ログイン状態の場合はホーム画面へ遷移
    if (user) navigate('/home');
  }, [user]);

  return (
    <>
      <Box sx={styles.header}>
        <Header />
      </Box>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.pageContainer}>
        <Typography component="div" sx={styles.container}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {bannerTitle && (
              <Box sx={styles.referralBanner}>
                <Typography sx={styles.referralBannerTitle}>{bannerTitle}</Typography>
                <Typography sx={styles.referralBannerText}>{bannerText}</Typography>
              </Box>
            )}
            <Typography component="div" sx={styles.inviteFormContainer}>
              <Typography sx={styles.title}>会員登録（無料）</Typography>
              <Controller
                render={({ field }): JSX.Element => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    formMb="24px"
                    helperText={errors.email?.message}
                    labelText={EMAIL_LABEL}
                    placeholder={EMAIL_INPUT_PLACEHOLDER}
                    requiredText={REQUIRED_LABEL}
                    width="400px"
                  />
                )}
                control={control}
                name="email"
              />
              <Typography sx={styles.CheckboxM} component="div">
                <Controller
                  render={({ field }): JSX.Element => (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            sx={{
                              color: orange50,
                              '&.Mui-checked': {
                                color: orange50,
                              },
                            }}
                          />
                        }
                        sx={{ marginLeft: 0 }}
                        label={
                          <>
                            <Link sx={linkColor} target="_blank" href={CORPORATE_TERMS_URL}>
                              利用規約
                            </Link>
                            <Typography component="span" sx={styles.termLabel}>
                              、
                            </Typography>
                            <Link sx={linkColor} target="_blank" href={CORPORATE_PRIVACY_URL}>
                              プライバシーポリシー
                            </Link>
                            <Typography component="span" sx={styles.termLabel}>
                              に同意する
                            </Typography>
                          </>
                        }
                      />
                      <FormHelperText
                        error={Boolean(errors.privacyCheck)}
                        sx={{
                          '&.Mui-error': {
                            color: red50,
                          },
                        }}
                      >
                        {checkboxHelper()}
                      </FormHelperText>
                    </FormGroup>
                  )}
                  control={control}
                  name="privacyCheck"
                />
              </Typography>

              <Button
                disabled={buttonDisabledFlag()}
                onClick={async (): Promise<void> => {
                  setFirstSubmit(true);
                  await handleSubmit(async () => onSubmit())();
                }}
                sx={[primaryButtonBackgroundColor, styles.button]}
                variant="contained"
              >
                会員登録（無料）
              </Button>
              <Link color="inherit" href="/" sx={[styles.link, { width: 'fit-content' }]}>
                ログインはこちら
              </Link>
            </Typography>
          </Box>
        </Typography>
        <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
      </Typography>
    </>
  );
};

export default AuthRegister;
